import { cloneDeep } from 'lodash';
import { type EventObject } from '../events';
import { createSurveyEvent } from './funnel-events';
import { reduceSurvey, type FunnelSurvey, type SurveyAction } from './survey';
import {
  type FunnelAnswerField,
  type FunnelAnswerUpdate,
  type TrackAnswerOptions,
} from '../session/types';

// Answer data comes from JSON contracts. Freeze the detached tree, never caller data.
const freezeAnswers = <T>(value: T): T => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.freeze(value);
    Object.values(value).forEach(freezeAnswers);
  }
  return value;
};

const toSurveyAction = (update: FunnelAnswerUpdate): SurveyAction => {
  switch (update.field) {
    case 'reason_for_leaving':
      return {
        type: 'setReasonForLeaving',
        ...update.value,
        livesOn: update.value.lives_on,
      };
    case 'responses':
      return {
        type: 'setModalResponse',
        modalName: update.modalName,
        message: update.value,
      };
    case 'competition_other':
      return update.value === undefined
        ? { type: 'clearCompetitionOther' }
        : { type: 'setSurveyField', key: update.field, value: update.value };
    case 'reason_other':
      return update.value === undefined
        ? { type: 'clearReasonOther' }
        : { type: 'setSurveyField', key: update.field, value: update.value };
    default:
      // Both mapped unions derive their key/value relationship from SurveyFields.
      return {
        type: 'setSurveyField',
        key: update.field,
        value: update.value,
      } as SurveyAction;
  }
};

type TrackingContext = Pick<
  Parameters<typeof createSurveyEvent>[0],
  'experience' | 'surveyType' | 'cardVersion'
>;

/** Shared answer owner. The host supplies commit timing, persistence and delivery. */
export const createFunnelAnswerSession = ({
  initialAnswers,
  getTrackingContext,
  dispatchEvent,
}: {
  initialAnswers: FunnelSurvey;
  getTrackingContext: () => TrackingContext;
  dispatchEvent: (event: EventObject) => void;
}) => {
  let answers = freezeAnswers(cloneDeep(initialAnswers));
  let disposed = false;
  const listeners = new Set<() => void>();
  const assertActive = () => {
    if (disposed) throw new Error('This funnel session has been disposed.');
  };
  const replaceAnswers = (next: FunnelSurvey): FunnelSurvey => {
    assertActive();
    const snapshot = freezeAnswers(cloneDeep(next));
    answers = snapshot;
    for (const listener of [...listeners]) {
      if (disposed) break;
      listener();
    }
    return snapshot;
  };

  return {
    // Internal callers use the existing survey contract; the public facade exposes
    // its deeply readonly view. Both see the same frozen snapshot.
    getAnswers: () => answers,
    replaceAnswers,
    setAnswer: (update: FunnelAnswerUpdate): FunnelSurvey => {
      assertActive();
      return replaceAnswers(reduceSurvey(answers, toSurveyAction(update)));
    },
    trackAnswer: (
      field: FunnelAnswerField,
      options: TrackAnswerOptions
    ): void => {
      assertActive();
      dispatchEvent(
        createSurveyEvent({
          ...getTrackingContext(),
          // Detach caller-supplied snapshots before deferred delivery can read them.
          survey: options.answers
            ? (freezeAnswers(cloneDeep(options.answers)) as FunnelSurvey)
            : answers,
          key: field,
          value: options.value,
          inputType: options.inputType,
        })
      );
    },
    subscribe: (listener: () => void) => {
      assertActive();
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    dispose: () => {
      disposed = true;
      listeners.clear();
    },
  };
};
